'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { PhoneIcon, PhoneXMarkIcon, MicrophoneIcon } from '@heroicons/react/24/solid';
import { Cog6ToothIcon, SpeakerWaveIcon, WrenchScrewdriverIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { useTwilioDevice } from '@hooks/useTwilioDevice';
import { getI18nLinkHref } from '@services/i18nPaths';
import type { RecruiterCallConnectParams } from '@type/recruiter-call';
import { Portal } from '@components/common/Portal/Portal';

interface RecruiterCallSoftphoneModalProps {
  open: boolean;
  candidateName: string;
  candidateUserId: number;
  jobSlug?: string | null;
  /** Cost in credits that will be charged at completion. 0 = free. */
  priceCredits?: number;
  onClose: () => void;
}

const formatDuration = (seconds: number) => {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

/**
 * Minimal softphone modal. Opens when the recruiter clicks "Call candidate",
 * mints a Twilio Voice token, initiates the call, and shows status +
 * duration + mute + hang-up. Recording + transcript + AI analysis are
 * handled server-side asynchronously — this modal only owns the *live*
 * call. Post-call artifacts render in a separate `RecruiterCallResult`
 * component on the candidate profile.
 */
export const RecruiterCallSoftphoneModal = ({
  open,
  candidateName,
  candidateUserId,
  jobSlug,
  priceCredits = 0,
  onClose,
}: RecruiterCallSoftphoneModalProps) => {
  const { t, i18n } = useTranslation('common');
  const locale = (i18n.language || 'ro') as Locale;
  const diagnosticsHref = getI18nLinkHref('/recruiter/call-diagnostics', locale);
  const {
    state, error, isMuted, callDuration, connect, disconnect, toggleMute,
    outputDevices, selectedOutputDeviceId, isOutputSelectionSupported,
    selectOutputDevice, testOutputDevice,
  } = useTwilioDevice();
  const [confirmed, setConfirmed] = useState(false);
  const [audioSettingsOpen, setAudioSettingsOpen] = useState(false);
  // Tracks whether this modal session ever reached an active-call state.
  // Used to auto-close the modal when the *remote* side hangs up (Twilio
  // fires `disconnect` → hook flips state back to `ready`, and without
  // this flag we can't tell "ended" apart from "never started").
  const wasActiveRef = useRef(false);

  useEffect(() => {
    if (state === 'connecting' || state === 'in-call') {
      wasActiveRef.current = true;
    }
  }, [state]);

  useEffect(() => {
    if (!open || !confirmed) return;
    if (state !== 'idle' && state !== 'ready' && state !== 'error') return;
    if (wasActiveRef.current) return; // don't re-dial after the call ended
    const params: RecruiterCallConnectParams = {
      candidateUserId: String(candidateUserId),
      jobSlug: jobSlug ?? '',
    };
    void connect(params);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, confirmed]);

  // Auto-close ~1.5s after Twilio reports the call ended — covers the
  // "candidate hung up first" case where the recruiter has no reason to
  // keep the modal open. Doesn't override the recruiter's own hang-up
  // (that path already calls onClose synchronously).
  useEffect(() => {
    if (!open) return undefined;
    if (!wasActiveRef.current) return undefined;
    if (state !== 'ready' && state !== 'error') return undefined;
    const timeoutId = setTimeout(() => onClose(), 1500);
    return () => clearTimeout(timeoutId);
  }, [state, open, onClose]);

  if (!open) return null;

  const statusLabel = (() => {
    switch (state) {
      case 'registering': return t('recruiter-call.status.registering');
      case 'connecting': return t('recruiter-call.status.connecting');
      case 'in-call': return t('recruiter-call.status.in_call', { duration: formatDuration(callDuration) });
      case 'ready': return wasActiveRef.current ? t('recruiter-call.status.ended') : t('recruiter-call.status.ready');
      case 'error': return t('recruiter-call.status.error');
      default: return '';
    }
  })();

  const handleHangUp = () => {
    disconnect();
    onClose();
  };

  return (
    // Portal to <body> so the modal escapes any transformed ancestor
    // (draggable card / animation wrapper), which would otherwise turn
    // `position: fixed` into a card-relative box + get clipped by the
    // card's `overflow-hidden`.
    <Portal wrapperId="recruiter-call-softphone-modal">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="recruiter-call-modal-title"
        // z-[80] stacks above the wrapping FlowModal / DetailModal that renders
        // the CV inside a Headless UI Dialog at z-[70] when the recruiter opens
        // the CV via a candidate card from the applicant-list flow. A plain
        // `z-50` here disappears behind that outer modal, so the softphone
        // popup renders but is not visible / clickable. Direct-URL loads have
        // no outer modal and worked coincidentally.
        className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50"
      >
      <div className="w-[92%] max-w-sm rounded-3xl bg-surface p-6 shadow-xl">
        <div className="relative mb-4 text-center">
          <div id="recruiter-call-modal-title" className="text-lg font-semibold text-ink">
            {candidateName}
          </div>
          {confirmed ? (
            <div className="mt-1 text-sm text-ink-medium">{statusLabel}</div>
          ) : (
            <div className="mt-1 text-sm text-ink-medium">
              {priceCredits > 0
                ? t('recruiter-call.price.paid', { credits: priceCredits })
                : t('recruiter-call.price.free')}
            </div>
          )}
          {isOutputSelectionSupported && (
            <button
              type="button"
              onClick={() => setAudioSettingsOpen((s) => !s)}
              aria-label={t('recruiter-call.audio.settings_toggle_aria', { defaultValue: 'Audio settings' })}
              aria-expanded={audioSettingsOpen}
              className="absolute right-0 top-0 flex size-8 items-center justify-center rounded-full text-ink-medium hover:bg-surface-100"
            >
              <Cog6ToothIcon className="size-5" />
            </button>
          )}
        </div>

        {audioSettingsOpen && isOutputSelectionSupported && (
          <div className="mb-4 rounded-2xl border border-input bg-surface-50 p-3">
            <label className="mb-1 flex items-center gap-2 text-xs font-semibold text-ink-medium">
              <SpeakerWaveIcon className="size-4" />
              {t('recruiter-call.audio.output_label', { defaultValue: 'Audio output (headset / speakers)' })}
            </label>
            <div className="flex items-center gap-2">
              <select
                value={selectedOutputDeviceId ?? ''}
                onChange={(e) => { void selectOutputDevice(e.target.value || null); }}
                className="w-full rounded-md border border-input bg-surface px-2 py-1.5 text-sm"
              >
                <option value="">
                  {t('recruiter-call.audio.default_option', { defaultValue: 'System default' })}
                </option>
                {outputDevices.map((d) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {d.label || t('recruiter-call.audio.unlabeled_device', { defaultValue: 'Unnamed device' })}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => { void testOutputDevice(); }}
                // Disabled only while a registration is already in flight
                // (avoids double-init). First click on a fresh session
                // triggers `ensureDevice()` under the hood — that grants
                // mic permission + populates device labels + then plays
                // the beep, all in one gesture.
                disabled={state === 'registering'}
                className="flex-shrink-0 rounded-md border border-input bg-surface px-3 py-1.5 text-xs font-medium text-ink hover:bg-surface-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t('recruiter-call.audio.test_button', { defaultValue: 'Test' })}
              </button>
            </div>
            {outputDevices.every((d) => !d.label) && (
              <p className="mt-2 text-xs text-ink-medium">
                {t(
                  'recruiter-call.audio.permission_hint',
                  { defaultValue: 'Device names appear after you grant microphone access on the first call.' },
                )}
              </p>
            )}
            {/* Always-available entry into the full diagnostics page.
                Second entry (prominent) lives inside the error banner below. */}
            <a
              href={diagnosticsHref}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-ink-medium underline decoration-dotted underline-offset-2 hover:text-ink"
            >
              <WrenchScrewdriverIcon className="size-3.5" />
              {t('recruiter-call.diagnostics.troubleshoot_link', { defaultValue: 'Troubleshoot audio + connection' })}
            </a>
          </div>
        )}

        {error && (
          <div className="mb-3 rounded-2xl bg-error/10 p-3 text-sm text-error">
            <div>{error}</div>
            <a
              href={diagnosticsHref}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-1 font-medium underline"
            >
              <WrenchScrewdriverIcon className="size-4" />
              {t('recruiter-call.diagnostics.troubleshoot_link', { defaultValue: 'Troubleshoot audio + connection' })}
            </a>
          </div>
        )}

        {!confirmed ? (
          <div className="mt-4 flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex h-11 min-w-24 items-center justify-center rounded-2xl border border-input bg-surface-50 px-4 text-sm font-medium text-ink-medium hover:bg-surface-100"
            >
              {t('recruiter-call.action.cancel')}
            </button>
            <button
              type="button"
              onClick={() => setConfirmed(true)}
              className="flex h-11 min-w-32 items-center justify-center gap-2 rounded-2xl bg-secondary px-4 text-sm font-semibold text-white shadow hover:bg-secondary/90"
            >
              <PhoneIcon className="size-4" />
              {t('recruiter-call.action.call_now')}
            </button>
          </div>
        ) : (
          <div className="mt-4 flex items-center justify-center gap-6">
            <button
              type="button"
              onClick={toggleMute}
              disabled={state !== 'in-call'}
              aria-pressed={isMuted}
              aria-label={isMuted ? 'Unmute' : 'Mute'}
              className={clsx(
                'flex size-14 items-center justify-center rounded-full border transition',
                isMuted
                  ? 'border-warning bg-warning/20 text-warning'
                  : 'border-input bg-surface-50 text-ink-medium hover:bg-surface-100',
                state !== 'in-call' && 'cursor-not-allowed opacity-50',
              )}
            >
              <MicrophoneIcon className="size-6" />
            </button>

            <button
              type="button"
              onClick={handleHangUp}
              aria-label="End call"
              className="flex size-16 items-center justify-center rounded-full bg-error text-white shadow-lg transition hover:bg-error/90"
            >
              <PhoneXMarkIcon className="size-7" />
            </button>
          </div>
        )}
        </div>
      </div>
    </Portal>
  );
};
